import { defineComponent as _defineComponent } from 'vue'
import { Ref, ref, computed, onUnmounted, onMounted, onBeforeMount } from 'vue';
import CDropdown from '@/components/CDropdown.vue';
import CInput from '../CInput';
import CButton from '../CButton/CButton.vue';
import CSolidColor from './CSolidColor.vue';
import Popper from 'popper.js';
import { type FullColorObject, type FullGradientObject, type GradientStopObject, GradientType, useColor } from '@/composables/color';
import { debounce } from 'lodash';

interface GradientStopSorted extends GradientStopObject {
    index: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CGradientColor',
  props: {
    value: null,
    mode: { default: 'rgb' }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: FullGradientObject): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: FullGradientObject, mode: 'cmyk' | 'rgb' };





const getGradientTypes = [
    {
        value: GradientType.LINEAR,
        text: 'Linear'
    },
    {
        value: GradientType.RADIAL,
        text: 'Radial'
    }
];

const gradientColorStopList = ref<GradientStopObject[]>([]);
const gradientAngle = ref(0);
const gradientType = ref<string>(GradientType.LINEAR);
const dragStopIndex = ref<number | null>(null);
const selectedStopIndex = ref<number | null>(null);
const containerRef: Ref<HTMLDivElement | null> = ref(null);
const containerGradientRef: Ref<HTMLDivElement | null> = ref(null);
const solidColorRef: Ref<HTMLDivElement | null> = ref(null);
const currentColor = ref<FullColorObject | undefined>();
const instancePopper = ref<Popper | null>(null);
const openColorPicker = ref(false);
const { getColorObj, getGradientCSS} = useColor();

const sortedGradientStops = computed<GradientStopSorted[]>(() => {
    return gradientColorStopList.value
        .map((stop, index) => ({
            ...stop,
            index
        }))
        .sort((a, b) => a.offset - b.offset);
});

const gradientStyle = computed(() => {
    return getGradientCSS(sortedGradientStops.value, gradientAngle.value);
});

const canDeleteStop = computed(() => {
    return gradientColorStopList.value.length > 2;
});

const getGradientStopPosition = (offset: number) => {
    const wStop = 14;
    return `calc(${offset}% - ${wStop / 2}px)`;
};

const innerStopBackground = (color: string) => {
    return `background-color: ${color};`;
};

const addColorStop = (event?: MouseEvent | TouchEvent) => {
    const container = containerRef.value;
    if (!container) return;

    let offset = 90;
    if (event) {
        const rect = container.getBoundingClientRect();
        const clientX = event instanceof MouseEvent ? event.clientX : (event as TouchEvent).touches[0].clientX;

        offset = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) * 100;
    } else if (sortedGradientStops.value.length >= 2) {
        const lastStop = sortedGradientStops.value[sortedGradientStops.value.length - 1];
        const secondLastStop = sortedGradientStops.value[sortedGradientStops.value.length - 2];
        offset = (lastStop.offset + secondLastStop.offset) / 2;
        offset = Math.min(Math.max(offset, 0), 100);
    }

    gradientColorStopList.value.push({ hex: '#ffffff', cmyk: { c: 0, m: 0, y: 0, k: 0}, alpha: 1, offset: Math.round(offset) });
    onInputChange();
};

const startDragging = (index: number, event: MouseEvent) => {
    event.preventDefault();
    dragStopIndex.value = index;
    setActiveStop(index);

    document.addEventListener('mousemove', handleDragging);
    document.addEventListener('mouseup', stopDragging);
};

const handleDragging = (event: MouseEvent | TouchEvent) => {
    if (dragStopIndex.value === null || !containerRef.value) return;

    const container = containerRef.value;
    const rect = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : (event as TouchEvent).touches[0].clientX;

    const offset = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) * 100;

    gradientColorStopList.value[dragStopIndex.value].offset = Math.round(offset);
    onInputChange();
};

const onKeyUp = (event: KeyboardEvent) => {
    if (event.key !== 'Delete' || selectedStopIndex.value === null) return;
    const stop = gradientColorStopList.value[selectedStopIndex.value];
    if (stop) {
        gradientColorStopList.value.splice(selectedStopIndex.value, 1);
        dragStopIndex.value = null;
        selectedStopIndex.value = null;
        onInputChange();
    }
};

const onDeleteStop = (index: number) => {
    if (index < 0 || index >= gradientColorStopList.value.length) return;
    if (gradientColorStopList.value.length <= 2) {
        console.warn('Cannot delete stop, at least two stops are required.');
        return;
    }
    gradientColorStopList.value.splice(index, 1);
    dragStopIndex.value = null;
    selectedStopIndex.value = null;
    onInputChange();
};

const onOffsetChange = (stop: GradientStopSorted, value: string | number) => {
    const oldStop = gradientColorStopList.value[stop.index];
    if (oldStop) {
        oldStop.offset = Math.round(Math.min(Math.max(Number(value), 0), 100));
    }
    onInputChange();
};

const onUpdateAngle = (value: string | number) => {
    const angle = Math.min(Math.max(Number(value), 0), 180);
    gradientAngle.value = angle;
    onInputChange();
};

const onGradientTypeChange = (value: GradientType) => {
    gradientType.value = value;
    onInputChange();
};

const unBindEvents = () => {
    document.removeEventListener('mousemove', handleDragging);
    document.removeEventListener('mouseup', stopDragging);
};

const stopDragging = () => {
    dragStopIndex.value = null;
    unBindEvents();
    onInputChange();
    onClosePopper();
};

const setActiveStop = (index: number) => {
    selectedStopIndex.value = index;
    const colorSelected = gradientColorStopList.value[index]
        ? gradientColorStopList.value[index]
        : undefined;

    currentColor.value = getColorObj({ hex: colorSelected?.hex, a: colorSelected?.alpha || 1 });
};

const onUpdateColor = (color: FullColorObject) => {
    if (selectedStopIndex.value !== null && gradientColorStopList.value[selectedStopIndex.value]) {
        gradientColorStopList.value[selectedStopIndex.value].hex = color.hex;
    }
    onInputChange();
};

const openColorPickerPopper = (index: number) => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    setActiveStop(index);
    if (!containerGradientRef.value) {
        console.warn('Container reference is not set.');
        return;
    }
    let container: HTMLDivElement = containerGradientRef.value;
    if (containerGradientRef.value?.parentElement) {
        container = containerGradientRef.value.parentElement as HTMLDivElement;
    }
    instancePopper.value = new Popper(container, solidColorRef.value as HTMLDivElement, {
        placement: 'top-end',
        modifiers: {
            flip: {
                enabled: false,
                behavior: ['top', 'bottom'],
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport', // prevent overflow on window
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0 // prevent overflow on right side
                }
            },
            hide: {
                enabled: false
            },
            offset: {
                offset: '100px, -25%'
            }
        }
    });
    openColorPicker.value = true;
};

const onClosePopper = () => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    openColorPicker.value = false;
};

const debounceUpdate = debounce(() => {
    const gradient: FullGradientObject = {
        colorStops: gradientColorStopList.value,
        angle: gradientAngle.value,
        type: gradientType.value as GradientType
    };
    emit('input', gradient);
}, 100);

const onInputChange = () => {
    debounceUpdate();
};

onMounted(() => {
    document.addEventListener('keyup', onKeyUp);
    if (props.value) {
        gradientColorStopList.value = props.value.colorStops || [];
        gradientAngle.value = props.value.angle || 0;
        gradientType.value = props.value.type || GradientType.LINEAR;
    } else {
        gradientColorStopList.value = [
            { hex: '#ffffff', cmyk: { c: 0, m: 0, y: 0, k: 0}, alpha: 1, offset: 0 },
            { hex: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 100}, alpha: 1, offset: 100 }
        ];
        gradientAngle.value = 0;
        gradientType.value = GradientType.LINEAR;
    }
    onInputChange();
});

onBeforeMount(() => {
    onInputChange();
});

onUnmounted(() => {
    unBindEvents();
    document.removeEventListener('keyup', onKeyUp);
});

return { __sfc: true,props, emit, getGradientTypes, gradientColorStopList, gradientAngle, gradientType, dragStopIndex, selectedStopIndex, containerRef, containerGradientRef, solidColorRef, currentColor, instancePopper, openColorPicker, getColorObj, getGradientCSS, sortedGradientStops, gradientStyle, canDeleteStop, getGradientStopPosition, innerStopBackground, addColorStop, startDragging, handleDragging, onKeyUp, onDeleteStop, onOffsetChange, onUpdateAngle, onGradientTypeChange, unBindEvents, stopDragging, setActiveStop, onUpdateColor, openColorPickerPopper, onClosePopper, debounceUpdate, onInputChange, CDropdown, CInput, CButton, CSolidColor }
}

})